/**
 * Notifications wrapper
 **/
import * as React from "react";

import {useGql} from "@khan/data-gql";
import Log, {Errors} from "@khan/logging";

import useProfile from "#data-access/use-profile.ts";

import type {
    clearBrandNewNotifications as GqlClearBrandNewNotifications,
    clearBrandNewNotificationsVariables as GqlClearBrandNewNotificationsVariables,
} from "./__graphql-types__/clearBrandNewNotifications.ts";
import type {
    getNotificationsForUser as GqlNotificationsForUser,
    getNotificationsForUserVariables as GqlNotificationsForUserVariables,
} from "./__graphql-types__/getNotificationsForUser.ts";
import {
    CLEAR_NOTIFICATIONS_MUTATION,
    NOTIFICATIONS_FOR_USER_QUERY,
} from "./notifications-queries.ts";
import {useNotificationsStore} from "./notifications-store.ts";

// Type added for fixture testing
export type NotificationsContextType = Omit<
    WithNotificationsProps,
    "notificationsLoading" | "notificationsCountLoading"
>;

// Context added for fixture testing
export const NotificationsContext: React.Context<
    NotificationsContextType | null | undefined
> = React.createContext<NotificationsContextType | null | undefined>(null);
NotificationsContext.displayName = "NotificationsContext";

/**
 * Hook that provides notifications functionality.
 *
 * This hook is responsible for:
 * 1. Providing a function to load the readable notifications.
 * 2. Keeping a reference of the loaded readable notifications for a logged-in
 *    user.
 * 3. Keeping a reference of the current new notifications count for a logged-in
 *    user
 * 4. Clearing the notifications count.
 */
export function useNotifications(): WithNotificationsProps {
    const {profile} = useProfile();
    const gqlFetch = useGql();
    const initalizedRef = React.useRef(false);
    const mockContext = React.useContext(NotificationsContext);

    const [_, setEverLoaded] = React.useState(false);
    const [hasMoreNotifications, setHasMoreNotifications] =
        React.useState(true);
    const [lastId, setLastId] = React.useState<string | null | undefined>(null);
    const [notificationsLoading, setNotificationsLoading] =
        React.useState(false);
    const [notifications, setNotifications] = React.useState<Array<any>>([]);
    const [notificationsCountLoading, setNotificationsCountLoading] =
        React.useState(false);
    const {hasNewNotifications, setHasNewNotifications} =
        useNotificationsStore();

    // Set whether there are new notifications from the profile on initial load.
    React.useEffect(() => {
        if (!initalizedRef.current && profile) {
            setHasNewNotifications(
                profile.get("countBrandNewNotifications") > 0,
            );
            initalizedRef.current = true;
        }
    }, [profile, setHasNewNotifications]);

    const _loadGqlNotifications = React.useCallback(() => {
        if (notificationsLoading || !hasMoreNotifications) {
            return;
        }

        setNotificationsLoading(true);
        setEverLoaded(true);

        const variables: GqlNotificationsForUserVariables = {
            after: lastId,
        };

        gqlFetch<GqlNotificationsForUser, GqlNotificationsForUserVariables>(
            NOTIFICATIONS_FOR_USER_QUERY,
            {
                variables,
            },
        )
            .then(async (data) => {
                const resultNotificationContainer = data.user?.notifications;

                if (resultNotificationContainer) {
                    const newNotifications: WithNotificationsProps["notifications"] =
                        [];

                    if (resultNotificationContainer.notifications) {
                        // NOTE: Notification Factory is quite large, we only
                        // import it when we need it.
                        const {getNotification} = await import(
                            "./notification-factory.ts"
                        );
                        for (const notification of resultNotificationContainer.notifications) {
                            const notificationObj =
                                await getNotification(notification);
                            if (notificationObj) {
                                newNotifications.push(notificationObj);
                            }
                        }
                    }

                    setNotifications((current) =>
                        current.concat(newNotifications),
                    );
                    const nextCursor =
                        resultNotificationContainer.pageInfo?.nextCursor ??
                        null;
                    setLastId(nextCursor);
                    setHasMoreNotifications(nextCursor != null);
                    setNotificationsLoading(false);
                }
            })
            .catch((error) => {
                Log.error("fetchNotificationsAPI error:", Errors.Internal, {
                    cause: error,
                });
                setNotificationsLoading(false);
            });
    }, [gqlFetch, hasMoreNotifications, lastId, notificationsLoading]);

    const loadNotifications = React.useCallback(() => {
        _loadGqlNotifications();
    }, [_loadGqlNotifications]);

    const clearNotifications = React.useCallback(() => {
        if (!hasNewNotifications) {
            return;
        }

        setNotificationsCountLoading(true);

        gqlFetch<
            GqlClearBrandNewNotifications,
            GqlClearBrandNewNotificationsVariables
        >(CLEAR_NOTIFICATIONS_MUTATION, {})
            .then((data) => {
                if (data.clearBrandNewNotifications?.error) {
                    Log.error(
                        "clearNotificationsAPI error: " +
                            data.clearBrandNewNotifications.error.code,
                        Errors.Internal,
                    );

                    setNotificationsCountLoading(false);
                } else {
                    setNotificationsCountLoading(false);
                    setHasNewNotifications(false);
                }
            })
            .catch((error) => {
                Log.error("clearNotificationsAPI error:", Errors.Internal, {
                    cause: error,
                });
                setNotificationsCountLoading(false);
            });
    }, [gqlFetch, hasNewNotifications, setHasNewNotifications]);

    return {
        notificationsLoading,
        notificationsCountLoading,
        notifications:
            mockContext && mockContext.notifications
                ? mockContext.notifications
                : notifications,
        hasMoreNotifications: mockContext
            ? mockContext.hasMoreNotifications
            : hasMoreNotifications,
        loadNotifications:
            mockContext && mockContext.loadNotifications
                ? mockContext.loadNotifications
                : loadNotifications,
        hasNewNotifications:
            mockContext && mockContext.hasNewNotifications != null
                ? mockContext.hasNewNotifications
                : hasNewNotifications,
        clearNotifications:
            mockContext && mockContext.clearNotifications
                ? mockContext.clearNotifications
                : clearNotifications,
    };
}

/**
 * This HOC injects the readable notifications and also injects a reference to
 * the `loadNotifications` function to the wrapped component.
 *
 * It also injects the notifications count as a `hasNewNotifications` prop and
 * also passes a reference to the `clearNotifications` function to the wrapped
 * component.
 *
 * This HOC assumes that your component is using exact props. Please update
 * your component to use exact props before using this HOC.
 *
 * Examples:
 * - export default withNotifications(WrappedComponent);
 * - export default compose(
 *      withNotifications,
 *      withRouter
 *   )(WrappedComponent);
 */

export type WithNotificationsProps = {
    // Set to true when SSR-ing or when waiting for notifications data on the
    // client.
    notificationsLoading: boolean;
    // The readable notifications list.
    // TODO(WEB-1597): Refactor this type after defining the GraphQL schema
    notifications: Array<any>;
    // Whether we have more notifications to load
    hasMoreNotifications: boolean;
    // Request more notifications from the server
    loadNotifications: () => unknown;
    // Set to true when SSR-ing or when waiting for new notifications count data
    // on the client.
    notificationsCountLoading: boolean;
    // Whether the user has new notifications
    hasNewNotifications: boolean;
    // clear new notifications
    clearNotifications: () => unknown;
};

export type WithoutNotifications<T> = Omit<T, keyof WithNotificationsProps>;

export default function withNotifications<
    C extends React.ComponentType<
        React.ComponentProps<C> & WithNotificationsProps
    > & {
        displayName?: string | undefined;
    },
    OuterProps = Omit<
        JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
        keyof WithNotificationsProps
    >,
>(WrappedComponent: C): React.ComponentType<OuterProps> {
    const ComponentWithNotifications = (props: OuterProps) => {
        const notificationsProps = useNotifications();

        return <WrappedComponent {...(props as any)} {...notificationsProps} />;
    };

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    ComponentWithNotifications.displayName = `withWithNotifications(${displayName})`;

    return ComponentWithNotifications;
}
